import { useEffect, useMemo } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { PostingsV3Mode } from '@revelio/data-access';

import { useCurrencyPreference } from '../../../shared/user-preferences/hooks/useCurrencyPreference';
import { ChartProvider } from '../../CustomDash/components/Chart/ChartProvider';
import { classifyChartDataRoute } from '../../CustomDash/components/Chart/ChartRoot/classify-chart-data-route';
import {
  DataProvider,
  MultiMetricDataProvider,
} from '../../CustomDash/components/DataProvider/DataProvider';
import { useRequestedPostingsMode } from '../../CustomDash/components/DataProvider/postings/postings-metric-mode';
import { SnowflakeDataProvider } from '../../CustomDash/components/DataProvider/SnowflakeDataProvider';
import { DataProviderQueryStatus } from '../../CustomDash/components/DataProvider/types';
import { getChartDataSourceAccessErrorMessage } from '../../CustomDash/components/DataProvider/utils/chart-data-source-access';
import { formatProviderErrorMessage } from '../../CustomDash/components/DataProvider/utils/data-provider-results-utils';
import { useTabContentConfig } from '../../CustomDash/components/PageConfigProvider/PageConfigProvider';
import type { ChartType } from '../../CustomDash/components/PageConfigProvider/types/chart';
import type { MetricValues } from '../../CustomDash/components/PageConfigProvider/types/metric';
import { useFilterContext } from '../../CustomDash/filters/contexts/FilterProvider';
import { usePlotMetric } from '../../CustomDash/hooks/usePlotMetric';
import {
  resolvedActivityLabelFromFilterMap,
  resolvedRoleLabelFromFilterMap,
  resolvedSkillLabelFromFilterMap,
} from '../global-ai-assistant-chart-preview-data-reporter';
import type { ChartDataDigest } from '../turn-outcome-digest';
import { buildChartDataDigest } from '../turn-outcome-digest';
import type { HeadlessDigestRequest } from './headless-digest-service';
import { resolveHeadlessDigest } from './headless-digest-service';

function errorDigest(
  metric: MetricValues | undefined,
  chartType: ChartType | undefined
): ChartDataDigest {
  return {
    kind: 'chart_data',
    // `?? 'chart'` (the same fallback the other synthetic digests use), NOT
    // String(undefined): the digest's metric is now NAMED in model-facing text,
    // so "undefined" would surface as `couldn't load the "Undefined" chart`.
    metric: metric ?? 'chart',
    chartType: String(chartType ?? 'unknown'),
    rowCount: 0,
    empty: true,
    error: true,
  };
}

/**
 * Consumes a data-provider render-prop status stream and resolves the request
 * once terminal (Error, or Success WITH a data payload — urql can emit Success
 * before the payload lands, same guard as GlobalAiChartPreviewDataReporter).
 * Renders nothing.
 */
function ProviderStatusSink({
  request,
  status,
  error,
  data,
  chartType,
  metric,
}: {
  request: HeadlessDigestRequest;
  status: DataProviderQueryStatus;
  error: unknown;
  data: unknown;
  chartType: ChartType;
  metric: MetricValues | undefined;
}) {
  // Resolved display labels of the applied role/skill/activity filters — same
  // source as the chart-preview reporter (the AI response only carries
  // taxonomy-relative numeric ids; this map is where the FE knows the
  // resolved names).
  const { activeFilterMap } = useFilterContext();
  const resolvedRoleLabel = useMemo(
    () => resolvedRoleLabelFromFilterMap(activeFilterMap),
    [activeFilterMap]
  );
  const resolvedSkillLabel = useMemo(
    () => resolvedSkillLabelFromFilterMap(activeFilterMap),
    [activeFilterMap]
  );
  const resolvedActivityLabel = useMemo(
    () => resolvedActivityLabelFromFilterMap(activeFilterMap),
    [activeFilterMap]
  );
  // Symbol only — the providers upstream already converted pay values at the
  // user's exchange rate, so this mirrors what the chart components do with
  // the same provider payload. See the note in the chart-preview reporter.
  const { code: currencyCode } = useCurrencyPreference();
  const postingsWeighting =
    useRequestedPostingsMode() === PostingsV3Mode.Hires
      ? ('expected_hires' as const)
      : undefined;
  useEffect(() => {
    // Opt-in status tracing for console probing (window.__gaHeadlessDebug=true):
    // distinguishes "provider never rendered" (no line at all) from "stuck
    // Fetching" (paused query) from "Success without payload" (shape gap).
    if (
      process.env.NODE_ENV === 'development' &&
      (window as { __gaHeadlessDebug?: boolean }).__gaHeadlessDebug
    ) {
      console.info(
        '[ga-headless-debug]',
        request.requestId.slice(0, 8),
        String(metric),
        status,
        data == null ? 'no-data' : 'has-data'
      );
    }
    if (status === DataProviderQueryStatus.Error) {
      if (process.env.NODE_ENV === 'development') {
        console.info(
          '[ga-headless] provider error',
          request.requestId,
          formatProviderErrorMessage(error)
        );
      }
      resolveHeadlessDigest(request.requestId, errorDigest(metric, chartType));
      return;
    }
    if (status === DataProviderQueryStatus.Success && data != null) {
      resolveHeadlessDigest(request.requestId, {
        ...buildChartDataDigest({
          data,
          // Same reason as `errorDigest` above — this metric names the chart in
          // every summary the model reads.
          metric: metric ?? 'chart',
          chartType,
          currencyCode,
          // Share-display inputs: a breakdown line over headcount PAINTS
          // shares, so the digest must describe shares too (see
          // isHeadcountShareDisplayedOnLine).
          secondaryDimension: request.item.plot?.secondaryDimension,
          displayAs: request.item.plot?.displayAs,
          postingsWeighting,
        }),
        ...(resolvedRoleLabel ? { resolvedRoleLabel } : {}),
        ...(resolvedSkillLabel ? { resolvedSkillLabel } : {}),
        ...(resolvedActivityLabel ? { resolvedActivityLabel } : {}),
      });
    }
  }, [
    status,
    error,
    data,
    chartType,
    metric,
    request,
    resolvedRoleLabel,
    resolvedSkillLabel,
    resolvedActivityLabel,
    currencyCode,
    postingsWeighting,
  ]);
  return null;
}

/**
 * Mirrors DataWrappedChart's provider routing (snowflake → multi-metric →
 * main) with none of its card/plot UI, branching off the SAME
 * `classifyChartDataRoute` the real chart tree uses. Data semantics are the
 * providers' own — this component only routes and reports.
 */
function HeadlessDigestProviderRouter({
  request,
}: {
  request: HeadlessDigestRequest;
}) {
  const { item } = request;
  const chartType = item.plot?.chartType;
  const metric = usePlotMetric();
  const { primaryFilter } = useTabContentConfig();
  const { loggedInUser } = useGetLoggedInUser();

  // Same route decision as the real chart tree (ChartRootSelector →
  // DataWrappedChart) — sharing the classifier is what keeps this mirror from
  // drifting, because drift here means a digest that hangs to its 30s timeout
  // and resolves EMPTY, ambiguous with a sick backend.
  const route =
    chartType === undefined ? undefined : classifyChartDataRoute(item);
  // Shapes the digest host doesn't support resolve an error digest instead of
  // hanging to the fetch timeout: tables and "special" charts (map, text,
  // entity-summary, …) have bespoke data pipelines outside DataWrappedChart.
  const unsupported =
    route === undefined || route === 'table' || route === 'special';
  // useGetLoggedInUser never returns null — it falls back to an EMPTY object
  // while the user-details query loads. Distinguish "not loaded yet" (wait;
  // the query's completion re-renders this leaf, and the fetch timeout
  // backstops) from "loaded without tabs" (blocked, per the access contract).
  // Without this gate an early fetch — e.g. the drawer-open warm-up racing
  // app boot — would one-shot-resolve a spurious no-access digest.
  const authLoaded = loggedInUser.id !== undefined;
  // Opt-in router tracing (window.__gaHeadlessDebug=true) — pairs with the
  // sink's status lines: a router line with NO following sink lines means the
  // provider tree rendered without ever calling its render-prop children.
  if (
    process.env.NODE_ENV === 'development' &&
    (window as { __gaHeadlessDebug?: boolean }).__gaHeadlessDebug
  ) {
    console.info(
      '[ga-headless-debug:router]',
      request.requestId.slice(0, 8),
      'metric=',
      String(metric),
      'chartType=',
      String(chartType),
      'primaryFilter=',
      String(primaryFilter?.id),
      'authLoaded=',
      authLoaded
    );
  }
  const accessErrorMessage =
    unsupported || chartType === undefined || !authLoaded
      ? undefined
      : getChartDataSourceAccessErrorMessage({
          metric,
          chartType,
          secondaryDimension: item.plot?.secondaryDimension,
          userTabs: loggedInUser.tabs ?? [],
        });
  const blocked = unsupported || accessErrorMessage !== undefined;

  useEffect(() => {
    if (blocked) {
      resolveHeadlessDigest(request.requestId, errorDigest(metric, chartType));
    }
  }, [blocked, request.requestId, metric, chartType]);

  if (blocked || chartType === undefined || !authLoaded) {
    return null;
  }

  // route === 'snowflake' implies the config is set (the classifier checked
  // it); the extra condition only narrows the type for the provider prop.
  if (route === 'snowflake' && item.plot?.snowflakeConfig) {
    return (
      <SnowflakeDataProvider
        config={item.plot.snowflakeConfig}
        chartType={chartType}
      >
        {({ data, error, status }) => (
          <ProviderStatusSink
            request={request}
            status={status}
            error={error}
            data={data}
            chartType={chartType}
            metric={metric}
          />
        )}
      </SnowflakeDataProvider>
    );
  }

  if (route === 'multi-metric') {
    return (
      <MultiMetricDataProvider
        item={item}
        chartType={chartType}
        primaryDimension={primaryFilter?.id}
      >
        {({ data, error, status }) => (
          <ProviderStatusSink
            request={request}
            status={status}
            error={error}
            data={data}
            chartType={chartType}
            metric={metric}
          />
        )}
      </MultiMetricDataProvider>
    );
  }

  return (
    <DataProvider
      chartType={chartType}
      primaryDimension={primaryFilter?.id}
      secondaryDimension={item.plot?.secondaryDimension}
      metric={metric}
    >
      {({ data, error, status }) => (
        <ProviderStatusSink
          request={request}
          status={status}
          error={error}
          data={data}
          chartType={chartType}
          metric={metric}
        />
      )}
    </DataProvider>
  );
}

/**
 * ChartProvider wrap mirrors `Chart`: `usePlotMetric` and several providers
 * read the item through `useChart()`, so the wrap is load-bearing.
 */
export function HeadlessDigestLeaf({
  request,
}: {
  request: HeadlessDigestRequest;
}) {
  return (
    <ChartProvider item={request.item}>
      <HeadlessDigestProviderRouter request={request} />
    </ChartProvider>
  );
}
